/* eslint-disable @typescript-eslint/no-explicit-any */
"use client"

import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import TypesenseInstantSearchAdapter, {
  TypesenseInstantsearchAdapterOptions,
} from "typesense-instantsearch-adapter"
import { useMutation } from "urql"
import { SearchFieldId } from "./Constants"
import { TypesenseContext } from "./TypesenseProvider"
import { getTypesenseKey, UserContext } from "./getTypesenseKey"

export interface useUiTypesenseClientProps {
  context?: UserContext
  collectionId?: SearchFieldId
  collectionName?: string
  usePermission?: boolean
  searchParams?: object
  useSourceTransfers?: boolean
}

interface TypesenseHookReturn {
  adapter?: TypesenseInstantSearchAdapter
  adapterProps?: TypesenseInstantsearchAdapterOptions
  apiKey: string
  exportCertificatesKey?: string
  hasError?: boolean
}

const MAX_RETRIES = 3

export const TYPESENSE_APIKEY_IN_STORAGE = "typesense-apiKey"
export const TYPESENSE_PARTY_APIKEY_IN_STORAGE = "typesense-party-apiKey"

/**
 * Get user-scoped localStorage key to prevent cross-session key leakage.
 * If userId is provided, key is scoped to that user.
 */
export const getUserScopedStorageKey = (
  baseKey: string,
  userId?: string,
): string => {
  return userId ? `${baseKey}-${userId}` : baseKey
}

/**
 * Clear all Typesense API keys for a specific user (call on logout).
 * If no userId provided, clears legacy unscoped keys.
 */
export const clearApiKey = (userId?: string) => {
  if (typeof window === "undefined") return

  // Clear user-scoped keys
  if (userId) {
    localStorage.removeItem(
      getUserScopedStorageKey(TYPESENSE_APIKEY_IN_STORAGE, userId),
    )
    localStorage.removeItem(
      getUserScopedStorageKey(TYPESENSE_PARTY_APIKEY_IN_STORAGE, userId),
    )
  }

  // Always clear legacy unscoped keys for backward compatibility
  localStorage.removeItem(TYPESENSE_APIKEY_IN_STORAGE)
  localStorage.removeItem(TYPESENSE_PARTY_APIKEY_IN_STORAGE)
}

// Buffer time before expiry to trigger proactive refresh (1 minute)
const NEAR_EXPIRY_BUFFER_MS = 60 * 1000

/**
 * Read cached Typesense key from localStorage synchronously.
 * Returns the key value if valid (not expired), otherwise null.
 *
 * Returns either:
 * - `apiKey` string for single-collection keys (e.g. "abc123")
 * - Stringified `keys` object for multi-collection keys (e.g. '{"products":"x","exports":"y"}')
 *
 * This matches the format expected by useTypesenseApiKey's setApiKey and the
 * mutation response handler, which stores/retrieves both formats the same way.
 */
export const getCachedKey = (localStorageKey: string): string | null => {
  if (typeof window === "undefined") return null
  try {
    const item = localStorage.getItem(localStorageKey)
    if (!item) return null
    const parsed = JSON.parse(item)
    if (!parsed?.expires) return null
    const expireTime = new Date(parsed.expires).getTime()
    if (expireTime < Date.now()) {
      localStorage.removeItem(localStorageKey)
      return null
    }
    // Return apiKey for single-collection, or stringified keys for multi-collection
    return parsed.apiKey || JSON.stringify(parsed.keys || {})
  } catch {
    return null
  }
}

/**
 * Check if the cached key is near expiry (within 1 minute buffer).
 * Used to trigger proactive refresh before key expires, avoiding 401 errors.
 */
export const isKeyNearExpiry = (localStorageKey: string): boolean => {
  if (typeof window === "undefined") return false
  try {
    const item = localStorage.getItem(localStorageKey)
    if (!item) return false
    const parsed = JSON.parse(item)
    if (!parsed?.expires) return false
    const expireTime = new Date(parsed.expires).getTime()
    const now = Date.now()
    return expireTime > now && expireTime < now + NEAR_EXPIRY_BUFFER_MS
  } catch {
    return false
  }
}

export function useTypesenseApiKey({
  mutation,
  localStorageKey,
  authTokenExpiresAt,
}: {
  mutation: string
  localStorageKey: string
  /** Unix timestamp (seconds) when auth token expires. When this changes, Typesense key is refreshed. */
  authTokenExpiresAt?: number
}) {
  const [apiKey, setApiKey] = useState(
    () => getCachedKey(localStorageKey) || "",
  )
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [hasError, setHasError] = useState(false)
  const isRefreshingRef = useRef(false)
  const retryCountRef = useRef(0)
  const prevAuthTokenExpiresAtRef = useRef<number | undefined>(
    authTokenExpiresAt,
  )
  const [newSearchApiKeyResult, updateNewSearchApiKeyResult] =
    useMutation(mutation)

  useEffect(() => {
    isRefreshingRef.current = isRefreshing
  }, [isRefreshing])

  const refreshKeys = useCallback(() => {
    if (isRefreshingRef.current) return
    if (retryCountRef.current >= MAX_RETRIES) return
    setIsRefreshing(true)
    localStorage.removeItem(localStorageKey)
    updateNewSearchApiKeyResult()
  }, [localStorageKey])

  // Refresh Typesense key when auth token is refreshed (authTokenExpiresAt changes)
  useEffect(() => {
    if (
      authTokenExpiresAt &&
      prevAuthTokenExpiresAtRef.current &&
      authTokenExpiresAt !== prevAuthTokenExpiresAtRef.current
    ) {
      // Auth token was refreshed — refresh Typesense key too
      refreshKeys()
    }
    prevAuthTokenExpiresAtRef.current = authTokenExpiresAt
  }, [authTokenExpiresAt, refreshKeys])

  useEffect(() => {
    // Skip if no mutation provided
    if (!mutation) return

    // Use getCachedKey for consistent expiry validation (avoids race condition
    // where lazy init and useEffect could disagree on key validity)
    const cachedKey = getCachedKey(localStorageKey)
    if (cachedKey) {
      // Key is valid — already set via lazy init, nothing more to do
      return
    }

    // No valid cached key — fetch a new one
    updateNewSearchApiKeyResult()
  }, [])

  useEffect(() => {
    if (newSearchApiKeyResult?.error) {
      console.error(
        `retrieving typesense key for ${localStorageKey} failed:`,
        newSearchApiKeyResult?.error,
      )
      retryCountRef.current += 1
      if (retryCountRef.current >= MAX_RETRIES) {
        setHasError(true)
      }
      setIsRefreshing(false)
      return
    }

    if (newSearchApiKeyResult?.data) {
      const multiCollectionData =
        newSearchApiKeyResult.data?.newMultiCollectionApiKeysV2
      const singleKeyData =
        newSearchApiKeyResult.data?.newSearchApiKey ||
        newSearchApiKeyResult.data?.newPartyApiKey

      if (multiCollectionData) {
        localStorage.setItem(
          localStorageKey,
          JSON.stringify({
            keys: multiCollectionData,
            expires: multiCollectionData.expires,
          }),
        )
        setApiKey(JSON.stringify(multiCollectionData))
        retryCountRef.current = 0
        setHasError(false)
      } else if (singleKeyData) {
        localStorage.setItem(
          localStorageKey,
          JSON.stringify({
            apiKey: singleKeyData.apiKey,
            expires: singleKeyData.expires,
          }),
        )
        setApiKey(singleKeyData.apiKey)
        retryCountRef.current = 0
        setHasError(false)
      } else {
        // Mutation succeeded but returned null data (e.g. OCA account suspended)
        // Treat as a terminal error — do not retry
        console.warn(
          `Typesense key mutation for ${localStorageKey} returned null data (account may be suspended)`,
        )
        setHasError(true)
      }
      setIsRefreshing(false)
    }
  }, [newSearchApiKeyResult.fetching])

  return { apiKey, refreshKeys, isRefreshing, hasError }
}

// 401 Error Handler for Typesense - per-adapter isolation
const createTypesenseAdapterWithErrorHandling = (
  config: any,
  onUnauthorized: () => void,
  checkNearExpiry?: () => boolean,
) => {
  const adapter = new TypesenseInstantSearchAdapter(config)

  const originalSearch = adapter.searchClient.search
  adapter.searchClient.search = async (requests: any) => {
    // Proactive check: trigger background refresh if key is near expiry (avoids 401)
    if (checkNearExpiry?.()) {
      onUnauthorized()
    }

    try {
      return await originalSearch.call(adapter.searchClient, requests)
    } catch (error: any) {
      if (error?.status === 401 || error?.message?.includes("401")) {
        onUnauthorized()
      }
      throw error
    }
  }

  return adapter
}

export function useTypesenseClient(
  props: useUiTypesenseClientProps,
): TypesenseHookReturn {
  const [adapter, setAdapter] = useState<TypesenseInstantSearchAdapter>()
  const [adapterProps, setAdapterProps] =
    useState<TypesenseInstantsearchAdapterOptions>()
  const [hasError, setHasError] = useState(false)
  const fieldRetryCountRef = useRef(0)

  const {
    typesenseHost,
    apiKey,
    multiCollectionKeysV2,
    multiKeyStorageKey,
    refreshKeys: contextRefreshKeys,
  } = useContext(TypesenseContext)

  const typesenseKey = getTypesenseKey({
    context: props.context,
    collectionId: props.collectionId,
    collectionName: props.collectionName,
    searchApiKey: apiKey,
    multiCollectionKeysV2: multiCollectionKeysV2,
    usePermissions: props.usePermission,
    useSourceTransfers: props.useSourceTransfers,
  })

  const handleFieldUnauthorized = useCallback(() => {
    fieldRetryCountRef.current += 1
    if (fieldRetryCountRef.current >= MAX_RETRIES) {
      setHasError(true)
      return
    }
    if (contextRefreshKeys) {
      contextRefreshKeys()
    }
  }, [contextRefreshKeys])

  // Reset field error when a new valid key arrives (successful refresh)
  useEffect(() => {
    if (typesenseKey && hasError) {
      fieldRetryCountRef.current = 0
      setHasError(false)
    }
  }, [typesenseKey])

  const searchParamsStr = useMemo(
    () => JSON.stringify(props.searchParams ?? {}),
    [props.searchParams],
  )

  useEffect(() => {
    if (!typesenseKey || !typesenseHost || hasError) {
      return
    }

    const serverConfig = {
      apiKey: typesenseKey,
      sendApiKeyAsQueryParam: false,
      nodes: [
        {
          url: `${typesenseHost}/search`,
        },
      ],
      connectionTimeoutSeconds: 30,
    }

    const config = {
      server: serverConfig,
      additionalSearchParameters: props.searchParams ?? {},
    }

    const typesenseAdapter = createTypesenseAdapterWithErrorHandling(
      config,
      handleFieldUnauthorized,
      multiKeyStorageKey
        ? () => isKeyNearExpiry(multiKeyStorageKey)
        : undefined,
    )

    setAdapter(typesenseAdapter)
    setAdapterProps(config)
  }, [
    typesenseKey,
    typesenseHost,
    searchParamsStr,
    hasError,
    multiKeyStorageKey,
  ])

  return {
    adapter,
    adapterProps: adapterProps,
    apiKey: typesenseKey || "",
    hasError,
  }
}
